import { useEffect, useState } from 'react';
import Popover from '../components/popover/Popover';
import api from 'services/api';
import { useSelector, useDispatch } from 'react-redux';
import { setSnackbar } from 'redux/snackbar';
import { ERROR_MESSAGES, filterMessageError } from './helpers';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import DateFnsUtils from '@date-io/date-fns';
import deLocale from 'date-fns/locale/pt-BR';
import AnalysisService from 'services/AnalysisService';

export function useAnalysis() {
  const selectedCycle = useSelector(
    (state) => state.selectedCycle.selectedCycle,
  );
  const [analysis, setAnalysis] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [openProgress, setOpenProgress] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorsWhenPosting, setErrorsWhenPosting] = useState([]);

  const dispatch = useDispatch();

  const columns = [
    {
      title: 'Viveiro',
      field: 'cycle.pond.description',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Data',
      field: 'get_at',
      type: 'date',
      editable: 'never',
      hideFilterIcon: true,
      dateSetting: { locale: 'pt-BR' },
      editComponent: (props) => (
        <LocalizationProvider adapterLocale={deLocale} dateAdapter={DateFnsUtils}>
          <DatePicker
            // inputFormat="DD/MM/YYYY"
            format="dd/MM/yyyy"
            value={props.value}
            onChange={props.onChange}
            InputProps={{
              style: {
                fontSize: 13,
              },
            }}
            fullWidth
          />
        </LocalizationProvider>
      ),
    },
    {
      title: 'Amostragem',
      field: 'size',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Parâmetros',
      field: 'samples',
      editable: 'never',
      render: (rowData) => {
        let parametersvalue = '';
        rowData.samples[0].parameters.forEach((parameter) => {
          parametersvalue
            += `${parameter.parameter.name
            }: ${
              parseFloat(parameter.value).toFixed(2)
            } ${
              parameter.parameter.unity.sigla
            }, `;
        });
        // return parameters;
        return <Popover data={parametersvalue.slice(0, -2)} />;
      },
      hideFilterIcon: true,
    },
  ];

  const columnsForExport = [
    {
      title: 'Ciclo',
      field: 'cycle',
    },
    {
      title: 'Viveiro',
      field: 'pond',
    },
    {
      title: 'Amostra',
      field: 'samples_number',
    },
    {
      title: 'Data',
      field: 'date',
    },
    {
      title: 'Parâmetros',
      field: 'parameter',
    },
    {
      title: 'Valor',
      field: 'value',
    },
  ];

  useEffect(() => {
    async function loadData() {
      try {
        setOpenProgress(false);
        const response = await api.get(
          `/healthmeasures/cycle-code/${selectedCycle.id}`,
        );

        if (response.data) {
          const data = await response.data;
          setAnalysis(data);
          setOpenProgress(true);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
      } finally {
        setOpenProgress(true);
      }
    }
    loadData();
  }, [selectedCycle]);

  useEffect(() => {
    async function loadHealthmeasuresExport() {
      try {
        const response = await (await api.get(`/healthmeasures/cycle-code/${selectedCycle.id}/export`)).data;

        if (response.length === 0) {
          setOpenProgress(true);
        }
        if (response) {
          setExportData(response);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
        setOpenProgress(true);
      }
    }
    loadHealthmeasuresExport();
  }, [selectedCycle]);

  const handleCloseSnackbar = () => {
    setErrorsWhenPosting([]);
  };

  // eslint-disable-next-line consistent-return
  const handleImportDataPost = async (payload) => {
    if (!payload) throw new Error('Payload is required.');
    try {
      setOpenProgress(false);
      const result = await AnalysisService.importData(payload);
      const {
        errorsData, importedData, status, successMessage,
      } = result;

      if (status === 201) {
        if (importedData.length === 0) {
          if (errorsData.length > 0) {
            return setErrorsWhenPosting(filterMessageError(errorsData));
          }
          return dispatch(
            setSnackbar(
              true,
              'error',
              ERROR_MESSAGES.EMPTY_DATA,
            ),
          );
        }
        if (importedData.length > 0) {
          const updatedList = await AnalysisService.healthmeasuresList(selectedCycle.id);
          setAnalysis(updatedList);
          dispatch(
            setSnackbar(
              true,
              'success',
              successMessage,
            ),
          );
          if (errorsData.length > 0) {
            setErrorsWhenPosting(filterMessageError(errorsData));
          }
          setOpenProgress(true);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || ERROR_MESSAGES.INSERT_NEW_DATA;
      dispatch(
        setSnackbar(
          true,
          'error',
          errorMessage,
        ),
      );
      throw new Error(errorMessage);
    }
  };

  return {
    analysis,
    columns,
    columnsForExport,
    exportData,
    openProgress,
    errors,
    errorsWhenPosting,
    handleCloseSnackbar,
    handleImportDataPost,
  };
}
